// Subscription plans: the ONE place a Contruly price, limit or gate is written.
//
// Two rails, two jobs: Contruly's own subscription is billed through Paddle
// (merchant of record, VAT handled); a creator's CLIENTS pay the creator through
// Stripe Connect, straight to the creator's bank, with Contruly taking 0 %. The
// two never meet. (Until 2026-11-01 this header named only Paddle while one
// feature line below named Stripe Connect, and the marketing page typed its
// own 39: three renderers of one fact.)
//
// Free = understand your business (internal tools)
// Pro = sell to clients (client-facing, revenue-generating tools)
//
// The marketing pages (/pricing, the home) read these objects and type no
// number of their own: src/parity/marketingReadsThePlanFile.parity.test.ts.

export const SUBSCRIPTION_PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Know your worth',
    price: 0,
    currency: 'EUR',
    interval: 'month',
    /** Free is a plan, not a trial: it does not end. */
    trialDays: 0,
    features: [
      'The catalog, with its five tiers of services, days and packages',
      'The full pricing pipeline, every step, with the price it derives',
      'Break-even, capacity and the booking-fee floor from your own numbers',
      'Equipment and expenses',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Contruly Pro',
    tagline: 'Sell With Confidence',
    /**
     * 60 a month since 2026-11-01 (Jota: Pro "should be more expensive than
     * Paperform… much closer to what I'm paying for Claude"; he agreed with
     * the math). Round, not 59: the product tells creators to round up to a
     * number that reads as chosen, and it says its own price the same way.
     */
    price: 60,
    /** A year paid at once: two months free, so 50 a month. */
    yearlyPrice: 600,
    /** Paddle adds each country's VAT at checkout; the page says so beside the price. */
    vatNote: '+ VAT where it applies',
    currency: 'EUR',
    interval: 'month',
    trialDays: 14,
    features: [
      'Everything in Free, with no limit on services or audiences',
      'A booking page clients pay on, with the price held while they decide',
      'Your own site and embeddable packages, generated from the catalog',
      'A client portal reading the terms they actually agreed to',
      'Payment plans, invoicing, and scope changes that price themselves',
      'Calendar sync, and reminders that fire without you',
      'Discount codes and referral codes',
      'Your clients, your jobs and the numbers behind them',
      'Up to 3 brands, 3 team members each',
    ],
  },
} as const;

/**
 * The Pioneers programme: the door through which the first fifty creators
 * come in, decided 2026-04-02 (memory launch_strategy; THE_LIST #22). The
 * price is locked for as long as the subscription runs; cancelling loses it.
 * Until the Paddle price exists, invitations run on grants with end dates.
 */
export const PIONEERS = {
  id: 'pioneers',
  name: 'Pioneers',
  /**
   * 39 since 2026-11-01 (Jota: "39"): the price Pro cost on the day the first
   * fifty were asked, kept for as long as they stay. It was 19 from 2026-04-02,
   * set as roughly half of a 39 Pro; with Pro at 60 that read as a hobby tool.
   */
  price: 39,
  currency: 'EUR',
  interval: 'month',
  seats: 50,
  /** Months of Pro at no charge while a pioneer sets up, before the 39€ starts. */
  setupMonthsFree: 3,
  /** The price holds for as long as the subscription is never cancelled. */
  lockedWhileSubscribed: true,
  status: 'invite-only',
} as const;

export type PlanId = keyof typeof SUBSCRIPTION_PLANS;

// Plan-based resource limits
export const PLAN_LIMITS = {
  free: {
    maxServices: 20,
    maxAudiences: 1,
    maxBrands: 1,
    maxMembersPerBrand: 1,
  },
  pro: {
    maxServices: Infinity,
    maxAudiences: Infinity,
    maxBrands: 3,
    maxMembersPerBrand: 3,
  },
} as const;

export type PlanLimits = typeof PLAN_LIMITS[keyof typeof PLAN_LIMITS];

// Feature keys that require Pro subscription.
// Free users get: service mapping, pricing engine, financial calculators, equipment inventory.
// Pro gates everything client-facing or revenue-generating.
export const PREMIUM_FEATURES = {
  preview_packages: {
    key: 'preview_packages',
    name: 'Preview Packages',
    description: 'Publish pricing pages your clients can see and book from',
    requiredPlan: 'pro' as PlanId,
  },
  embed_packages: {
    key: 'embed_packages',
    name: 'Embeddable Pricing',
    description: 'Drop pricing widgets on your own website',
    requiredPlan: 'pro' as PlanId,
  },
  booking_wizard: {
    key: 'booking_wizard',
    name: 'Booking Wizard',
    description: 'Full 10-step booking flow with online payments',
    requiredPlan: 'pro' as PlanId,
  },
  client_portal: {
    key: 'client_portal',
    name: 'Client Portal',
    description: 'Post-booking hub with invoices, deliverables, and timeline',
    requiredPlan: 'pro' as PlanId,
  },
  payment_processing: {
    key: 'payment_processing',
    name: 'Payment Processing',
    description: 'Accept payments directly through Stripe Connect',
    requiredPlan: 'pro' as PlanId,
  },
  google_calendar_sync: {
    key: 'google_calendar_sync',
    name: 'Google Calendar Sync',
    description: 'Two-way calendar sync with automatic event creation',
    requiredPlan: 'pro' as PlanId,
  },
  workflow_automation: {
    key: 'workflow_automation',
    name: 'Workflow Automation',
    description: 'Email sequences and reminders triggered by booking events',
    requiredPlan: 'pro' as PlanId,
  },
  discount_codes: {
    key: 'discount_codes',
    name: 'Discount Codes',
    description: 'Client referrals, partner codes, and special discounts',
    requiredPlan: 'pro' as PlanId,
  },
  client_crm: {
    key: 'client_crm',
    name: 'Client CRM',
    description: 'Client management with relationship stages and lifetime value',
    requiredPlan: 'pro' as PlanId,
  },
  job_log: {
    key: 'job_log',
    name: 'Job Log',
    description: 'Per-booking profit and loss tracking',
    requiredPlan: 'pro' as PlanId,
  },
  business_intelligence: {
    key: 'business_intelligence',
    name: 'Business Intelligence',
    description: 'Revenue trends, seasonal patterns, and service analytics',
    requiredPlan: 'pro' as PlanId,
  },
  vendor_partnerships: {
    key: 'vendor_partnerships',
    name: 'Vendor Partnerships',
    description: 'Hire and be hired by other creative professionals',
    requiredPlan: 'pro' as PlanId,
  },
  multi_audience: {
    key: 'multi_audience',
    name: 'Multiple Audiences',
    description: 'Different pricing pages for couples, corporate, and more',
    requiredPlan: 'pro' as PlanId,
  },
  multi_brand: {
    key: 'multi_brand',
    name: 'Multiple Brands',
    description: 'Run up to 3 separate brand catalogs',
    requiredPlan: 'pro' as PlanId,
  },
  custom_branding: {
    key: 'custom_branding',
    name: 'Custom Branding',
    description: 'Brand the client portal and showcase pages',
    requiredPlan: 'pro' as PlanId,
  },
  export_settings: {
    key: 'export_settings',
    name: 'Export Settings',
    description: 'Export all business configuration, services, and pricing data',
    requiredPlan: 'pro' as PlanId,
  },
} as const;

export type FeatureKey = keyof typeof PREMIUM_FEATURES;
